import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  CreatedPatient,
  Patient,
  PatientCreatePayload,
  PatientRepositoryService,
  PatientUpdatePayload
} from '@xmedical/core';
import { PracticeStore } from './practice.store';

/**
 * Failure shape exposed by `loadError`. Replaces the legacy
 * `ErrorEvent` class from PatientEventsService — kept inline here because
 * this is the only place a load failure is published.
 */
export interface PatientLoadError {
  type: 'patient';
  message: string;
  httpError?: unknown;
}

/**
 * PatientStore owns the canonical "current patient" signal. Sub-stores and
 * components read `current()` directly; writes go through `setCurrent` (load,
 * create re-fetch, update re-fetch).
 */
@Injectable({
  providedIn: 'root'
})
export class PatientStore {
  private practiceStore = inject(PracticeStore);
  private patientRepo = inject(PatientRepositoryService);

  private readonly _current = signal<Patient | null>(null);
  readonly current: Signal<Patient | null> = this._current.asReadonly();

  /**
   * Replace the current patient.
   */
  setCurrent(patient: Patient | null): void {
    this._current.set(patient);
  }

  readonly isEditing = signal<boolean>(false);

  /**
   * Counter that increments every time `requestSave()` is called. The patient
   * form subscribes to this via an effect and triggers its own `save()` when
   * the counter advances. Replaces the legacy `trigger_save` event so the
   * top-nav save icon and the form save button share a signal-based contract
   * instead of routing through the patient event bus.
   */
  readonly saveCommand = signal<number>(0);

  /**
   * Pending status update from the top-nav status dropdown while the edit
   * form is mounted. The form picks up the new id via an effect and patches
   * its status control (the change becomes part of the user's unsaved edits).
   * `serial` advances on every request so re-selecting the same id still
   * triggers the form effect. Replaces the legacy `patient_status_updated`
   * event.
   *
   * Always non-null — `serial` 0 means "no request issued yet" so a freshly
   * mounted form can capture the current value as its baseline without
   * incorrectly treating a stale push as a live one.
   */
  readonly statusUpdateCommand = signal<{ id: number; serial: number }>({ id: 0, serial: 0 });

  /**
   * Last error from `load()`. Cleared on a successful load. Components that
   * reacted to the legacy `patientEventsService.error$` for type === 'patient'
   * now consume this signal directly. Save errors (create / update) flow back
   * through the returned observable's error callback at the call site, so they
   * are not represented here.
   */
  private readonly _loadError = signal<PatientLoadError | null>(null);
  readonly loadError = this._loadError.asReadonly();

  /**
   * Bump the save counter — equivalent to the legacy
   * `PatientEvent('trigger_save')` emit.
   */
  requestSave(): void {
    this.saveCommand.update(v => v + 1);
  }

  /**
   * Push a status id into the edit form. Equivalent to the legacy
   * `PatientEvent('patient_status_updated', id)` emit; only meaningful when
   * the edit form is mounted.
   */
  requestEditStatusUpdate(id: number): void {
    this.statusUpdateCommand.update(prev => ({
      id,
      serial: prev.serial + 1
    }));
  }

  readonly displayName = computed<string>(() => {
    const patient = this.current();
    if (!patient) return '';
    if (patient.nickname) {
      return `${patient.firstName} ( ${patient.nickname} ) ${patient.surname}`;
    }
    return patient.name ?? '';
  });

  readonly displayProfileId = computed<string>(() => {
    const patient = this.current();
    if (!patient) return '';
    const useFileNumber =
      this.practiceStore.practiceSettings()?.useFileNumberAsPatientProfileId ?? false;
    const raw = useFileNumber
      ? patient.fileNumber ?? ''
      : patient.profileId?.toString() ?? '';
    return raw.padStart(4, '0');
  });

  readonly isFlagged = computed<boolean>(() => this.current()?.isFlagged ?? false);
  readonly isDeleted = computed<boolean>(() => this.current()?.isDeleted ?? false);

  /**
   * Per-patient Pusher channel for the current patient, or null when no
   * patient is loaded. Sub-stores subscribe to events on this channel inside
   * effects so push handlers automatically rebind on patient switch.
   */
  readonly patientEventsChannelName = computed<string | null>(() => {
    const patient = this.current();
    const practiceId = this.practiceStore.selectedPracticeId();
    if (!patient || !practiceId) return null;
    return `xmedical_practice_${practiceId}_patients_${patient.id}_events`;
  });

  /**
   * Load a patient by profile ID and hydrate the store.
   */
  load(profileId: number): Observable<Patient> {
    return this.patientRepo.loadByProfileId(profileId).pipe(
      tap({
        next: patient => {
          this._loadError.set(null);
          this.setCurrent(patient);
        },
        error: error => {
          this._loadError.set({
            type: 'patient',
            message: 'Could not load patient details',
            httpError: error
          });
        }
      })
    );
  }

  /**
   * Flag the current patient. Mirrors the legacy PatientService behaviour:
   * mutates the in-flight patient object on success so existing @Input-based
   * consumers see the new isFlagged value without a re-emit. Phase 5 will
   * tighten this once consumers read directly from the store signal.
   */
  flag(): Observable<boolean> {
    const patient = this.requireCurrent('flag');
    return this.patientRepo.flag(patient.id).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
          patient.isFlagged = true;
        }
      })
    );
  }

  /**
   * Clear the flag on the current patient. See `flag()` for the in-place
   * mutation rationale.
   */
  unflag(): Observable<boolean> {
    const patient = this.requireCurrent('unflag');
    return this.patientRepo.unFlag(patient.id).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
          patient.isFlagged = false;
        }
      })
    );
  }

  /**
   * Update the flagged-comment text on the current patient. Matches the
   * legacy behaviour of writing the new comment locally before the request
   * resolves so the UI stays responsive.
   */
  updateFlaggedComment(comment: string): Observable<boolean> {
    const patient = this.requireCurrent('updateFlaggedComment');
    const previousComment = patient.flaggedComment;
    patient.flaggedComment = comment;
    return this.patientRepo.updateFlaggedComment(patient.id, comment).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
        },
        error: () => {
          // Roll back the optimistic local write so the UI never shows a
          // comment the server rejected.
          patient.flaggedComment = previousComment;
        }
      })
    );
  }

  /**
   * Update the flagged-comment for an arbitrary patient by id, independent of
   * the current-patient signal. Exists for the dashboard flagged-patients
   * widget, which edits comments across a list of patients rather than the
   * single open one. Stateless passthrough — keeps components calling the
   * store instead of injecting the repository directly ("The Way"). The
   * caller owns its own optimistic UI / rollback since these rows are not the
   * current-patient state.
   */
  updateFlaggedCommentFor(id: number, comment: string): Observable<boolean> {
    return this.patientRepo.updateFlaggedComment(id, comment);
  }

  /**
   * Soft-delete the current patient. Optimistic — flips isDeleted before the
   * request resolves so the UI updates immediately, and rolls back on error.
   */
  delete(): Observable<boolean> {
    const patient = this.requireCurrent('delete');
    const previousDeletedDate = patient.deletedDate;
    const previousIsDeleted = patient.isDeleted;
    patient.deletedDate = new Date();
    patient.isDeleted = true;
    return this.patientRepo.delete(patient.id).pipe(
      tap({
        error: () => {
          patient.deletedDate = previousDeletedDate;
          patient.isDeleted = previousIsDeleted;
        }
      })
    );
  }

  /**
   * Undo a soft-delete on the current patient. Optimistic with rollback.
   */
  undelete(): Observable<boolean> {
    const patient = this.requireCurrent('undelete');
    const previousDeletedDate = patient.deletedDate;
    const previousIsDeleted = patient.isDeleted;
    patient.deletedDate = null;
    patient.isDeleted = false;
    return this.patientRepo.undelete(patient.id).pipe(
      tap({
        error: () => {
          patient.deletedDate = previousDeletedDate;
          patient.isDeleted = previousIsDeleted;
        }
      })
    );
  }

  /**
   * Add a practice flag to the current patient. Stamps lastModifiedDate on
   * success to mirror the legacy behaviour. The patientFlags list itself is
   * owned by PatientService for now; PatientFlagsStore takes over in Phase 3.
   */
  addFlag(flagId: number): Observable<boolean> {
    const patient = this.requireCurrent('addFlag');
    return this.patientRepo.addFlagToPatient(patient.id, flagId).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
        }
      })
    );
  }

  /**
   * Remove a practice flag from the current patient.
   */
  removeFlag(flagId: number): Observable<boolean> {
    const patient = this.requireCurrent('removeFlag');
    return this.patientRepo.removeFlagFromPatient(patient.id, flagId).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
        }
      })
    );
  }

  /**
   * Replace the entire set of practice flags assigned to the current patient.
   */
  updatePatientFlags(flagIds: number[]): Observable<boolean> {
    const patient = this.requireCurrent('updatePatientFlags');
    return this.patientRepo.updatePatientFlags(patient.id, flagIds).pipe(
      tap({
        next: () => {
          patient.lastModifiedDate = new Date();
          patient.modifiedDateUtc = new Date();
        }
      })
    );
  }

  /**
   * Create a new patient. POST returns the small `{id, profileId, ...}` DTO
   * (CreatedPatient), not the full read shape. The store re-fetches by
   * profileId and seeds `currentPatient` with the canonical Patient. Returns
   * the original CreatedPatient response so callers can navigate using
   * profileId without waiting for the re-fetch.
   *
   * Errors emit a `patient` ErrorEvent and re-throw so the form can react.
   */
  create(payload: PatientCreatePayload): Observable<CreatedPatient> {
    return this.patientRepo.create(payload).pipe(
      switchMap(created =>
        this.patientRepo.loadByProfileId(created.profileId).pipe(
          tap(patient => this.setCurrent(patient)),
          // Re-fetch is best-effort: if hydration fails the create itself
          // still succeeded, so swallow the hydrate error and return the
          // CreatedPatient so callers can navigate.
          catchError(() => of(null)),
          // Always return the CreatedPatient regardless of hydrate outcome.
          switchMap(() => of(created))
        )
      )
    );
  }

  /**
   * Update the current patient. PUT returns just a success boolean, so the
   * store re-fetches by profileId to refresh `currentPatient` with the
   * canonical read shape (status: PatientStatus object, latest
   * lastModifiedDate, etc.). Resolves with the boolean from the PUT.
   */
  update(payload: PatientUpdatePayload): Observable<boolean> {
    const patient = this.requireCurrent('update');
    return this.patientRepo.update(patient.id, payload).pipe(
      switchMap(success =>
        this.patientRepo.loadByProfileId(patient.profileId).pipe(
          tap(refreshed => this.setCurrent(refreshed)),
          catchError(() => of(null)),
          switchMap(() => of(success))
        )
      )
    );
  }

  /**
   * Convenience for the patient-top-nav status dropdown — applies a new
   * status id to the current patient via `update()`. The full update payload
   * is constructed from the current patient's existing values (no form
   * involvement, since the top-nav has no form).
   */
  updateStatus(statusId: number): Observable<boolean> {
    const patient = this.requireCurrent('updateStatus');
    const payload = buildStatusUpdatePayload(patient, statusId);
    return this.update(payload);
  }

  /**
   * Clear the current patient and all per-domain sub-state.
   *
   * Sub-stores listen for `current()` going null via their effects and
   * clear their own per-domain state.
   */
  clear(): void {
    this.setCurrent(null);
  }

  private requireCurrent(operation: string): Patient {
    const patient = this.current();
    if (!patient) {
      throw new Error(`PatientStore.${operation}() called with no current patient`);
    }
    return patient;
  }
}

/**
 * Build a PatientUpdatePayload that changes only the status, preserving every
 * other field from the current patient. Used by PatientStore.updateStatus()
 * for the patient-top-nav status dropdown — the form is not involved.
 *
 * The flag fields (isFlagged / flaggedComment / flaggedDate) are intentionally
 * not on the wire — the dedicated flag endpoints own them and the server
 * ignores them on the general PUT (see PatientUpdatePayload).
 */
function buildStatusUpdatePayload(patient: Patient, statusId: number): PatientUpdatePayload {
  return {
    title: patient.title,
    firstName: patient.firstName,
    surname: patient.surname,
    middleNames: patient.middleNames,
    nickname: patient.nickname,
    identityNumber: patient.identityNumber,
    dateOfBirth: new Date(patient.dateOfBirth),
    gender: patient.gender,
    passportNumber: patient.passportNumber,
    homeLanguage: patient.homeLanguage,
    race: patient.race,
    fileNumber: patient.fileNumber,
    profilePictureUrl: patient.profilePictureUrl,
    emailAddresses: (patient.emailAddresses ?? []).map(e => ({
      type: e.type,
      label: (e as unknown as { label?: string; description?: string }).label
        ?? (e as unknown as { description?: string }).description ?? '',
      emailAddress: e.emailAddress,
    })),
    contactNumbers: (patient.contactNumbers ?? []).map(c => ({
      type: c.type,
      label: (c as unknown as { label?: string; description?: string }).label
        ?? (c as unknown as { description?: string }).description ?? '',
      number: c.number,
    })),
    addressDetails: (patient.addressDetails ?? []).map(a => ({
      type: a.type, label: a.label, address: a.address,
    })),
    secondaryContacts: (patient.secondaryContacts ?? []).map(s => ({
      firstName: s.firstName,
      surname: s.surname,
      relationToPatient: s.relation,
      contactNumbers: (s.contactNumbers ?? []).map(c => ({
        type: c.type,
        label: (c as unknown as { label?: string; description?: string }).label
          ?? (c as unknown as { description?: string }).description ?? '',
        number: c.number,
      })),
      emailAddresses: [],
      addressDetails: [],
    })),
    familyDoctors: (patient.familyDoctors ?? []).map(d => ({
      name: d.name,
      practiceNumber: d.practiceNumber,
      tel: d.tel,
      fax: d.fax,
      email: d.email,
      preferredGreeting: d.preferredGreeting,
      preferredContactMethod: d.preferredContactMethod,
    })),
    referringDoctors: (patient.referringDoctors ?? []).map(d => ({
      name: d.name,
      practiceNumber: d.practiceNumber,
      tel: d.tel,
      fax: d.fax,
      email: d.email,
      preferredGreeting: d.preferredGreeting,
      preferredContactMethod: d.preferredContactMethod,
    })),
    billingContact: patient.billingContact ? {
      billingContactFirstName: patient.billingContact.billingContactFirstName,
      billingContactSurname: patient.billingContact.billingContactSurname,
      billingContactIdentityNumber: patient.billingContact.billingContactIdentityNumber,
      billingContactContactNumbers: (patient.billingContact.billingContactContactNumbers ?? []).map(c => ({
        type: c.type,
        label: (c as unknown as { label?: string; description?: string }).label
          ?? (c as unknown as { description?: string }).description ?? '',
        number: c.number,
      })),
      billingContactEmailAddresses: (patient.billingContact.billingContactEmailAddresses ?? []).map(e => ({
        type: e.type,
        label: (e as unknown as { label?: string; description?: string }).label
          ?? (e as unknown as { description?: string }).description ?? '',
        emailAddress: e.emailAddress,
      })),
      billingContactAddressDetails: (patient.billingContact.billingContactAddressDetails ?? []).map(a => ({
        type: a.type, label: a.label, address: a.address,
      })),
    } : null,
    medicalAid: patient.medicalAid ?? null,
    status: statusId,
    statusId,
  };
}
